// frontend/src/pages/ForgotPassword.jsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { useLanguage } from '../utils/i18n';
import LanguageToggle from '../components/LanguageToggle';
import Logo from '../components/Logo';
import { ArrowLeft } from 'lucide-react';

export default function ForgotPassword() {
    const { t } = useLanguage();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    async function handleSubmit(e) {
        e.preventDefault();
        if (!email) return;
        setError('');
        setLoading(true);

        try {
            const { api } = await import('../utils/api');
            const lang = localStorage.getItem('appLang') || 'en';

            await api.forgotPassword({ email, lang });
            setSuccess(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)', position: 'relative' }}>
            {/* Responsive Header */}
            <header className="header-container" style={{ position: 'relative', zIndex: 10 }}>
                <Link to="/" className="btn btn-outline glass-btn header-back-minimal" style={{ textDecoration: 'none' }}>
                    <ArrowLeft size={18} /> <span className="hide-mobile" style={{ marginLeft: 8 }}>{t('back_to_home')}</span>
                </Link>

                <div className="header-centered-logo">
                    <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
                        <Logo size={24} />
                        <span style={{ fontWeight: 800, fontSize: 18, color: 'var(--text)', letterSpacing: -0.5 }}>Wafiyo</span>
                    </Link>
                </div>

                <LanguageToggle />
            </header>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', position: 'relative' }}>
                <div className="bg-shapes hide-mobile">
                    <div className="shape shape-1" style={{ top: '-10%', left: '-10%', width: 600, height: 600, opacity: 0.15 }}></div>
                    <div className="shape shape-2" style={{ bottom: '-10%', right: '-10%', width: 800, height: 800, opacity: 0.1 }}></div>
                </div>

                <div className="glass-card" style={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1 }}>
                    <div style={{ textAlign: 'center', marginBottom: 28 }}>
                        <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>{t('forgot_pw_title')}</h1>
                        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>
                            {t('forgot_pw_subtitle')}
                        </p>
                    </div>

                    {error && <div className="alert alert-error">{error}</div>}

                    {success ? (
                        <div style={{ textAlign: 'center', padding: '20px 0' }}>
                            <div style={{ fontSize: 48, marginBottom: 16 }}>📩</div>
                            <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 12, lineHeight: 1.5 }}>
                                {t('forgot_pw_success')}
                            </h2>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} noValidate>
                            <div className="form-group">
                                <label>{t('email')}</label>
                                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@email.com" required />
                            </div>
                            <button className="btn btn-primary btn-glow" type="submit" disabled={loading} style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}>
                                {loading ? t('please_wait') : t('send_link')}
                            </button>
                        </form>
                    )}

                    <div style={{ textAlign: 'center', marginTop: 24 }}>
                        <Link to="/login" style={{ color: '#8b949e', fontSize: 14, textDecoration: 'none', fontWeight: 500 }}>
                            {t('back_to_login')}
                        </Link>
                    </div>
                </div>
            </div>

            {/* Standard Footer */}
            <footer className="landing-footer" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="container">
                    <div className="footer-content">
                        <div className="footer-brand">
                            <Logo size={28} />
                            <span className="logo-text">Wafiyo</span>
                            <p className="footer-tagline">
                                {t('footer_tagline')}
                            </p>
                        </div>
                        <div className="footer-links">
                            <div className="link-col">
                                <h4>{t('legal')}</h4>
                                <Link to="/privacy">{t('privacy_policy_title')}</Link>
                                <Link to="/terms">{t('tos_title')}</Link>
                                <Link to="/refund">{t('refund_policy_title')}</Link>
                                <Link to="/contact">{t('contact_title')}</Link>
                            </div>
                        </div>
                    </div>
                    <div className="footer-bottom">
                        <p>© {new Date().getFullYear()} Wafiyo. {t('all_rights_reserved')}</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
